import ProductDescriptionTab from './tabs/ProductDescriptionTab';
import ProductFitmentTab from './tabs/ProductFitmentTab';
import { ProductDocument, Variation } from 'models/product';
import { BreadcrumbItem } from '@ui/Breadcrumb';
import { useAuth } from 'context/AuthContext';
import PartNumbersTab from './tabs/PartNumberTab';
import SizingFitTab from './tabs/SizingFitTab';

type ProductInfoTabsProps = {
  product: ProductDocument;
  breadcrumbs: BreadcrumbItem[];
  fitment: Map<
    string,
    Map<
      string,
      {
        years: number[];
        rideType: string;
      }
    >
  >;
  selectedVariations: Variation[] | null;
};

export default function ProductInfoTabs({
  product,
  breadcrumbs,
  fitment,
  selectedVariations,
}: ProductInfoTabsProps) {
  const { userDoc } = useAuth();

  const tabs = [
    {
      id: 'description',
      title: 'Product Description',
      shortTitle: 'Description',
      content: (
        <ProductDescriptionTab
          product={product}
          breadcrumbs={breadcrumbs}
          selectedVariations={selectedVariations}
        />
      ),
    },
    {
      id: 'fitment',
      title: 'Fitment',
      shortTitle: '',
      content: <ProductFitmentTab product={product} userDoc={userDoc} />,
    },
    {
      id: 'part-numbers',
      title: 'Part Numbers',
      shortTitle: '',
      content: <PartNumbersTab product={product} />,
    },
    {
      id: 'sizing-fit',
      title: 'Sizing & Fit',
      shortTitle: '',
      content: <SizingFitTab product={product} />,
    },
  ] as const;

  const visibleTabs = tabs.filter((tab) => {
    if (tab.title === 'Fitment') return product.fitting_bikes?.bikes?.length;
    if (tab.title === 'Part Numbers')
      return (
        product.variations?.some((v) => v.mpn?.length) || product.mpn?.length
      );
    if (tab.title === 'Sizing & Fit')
      return (
        product.category1?.toLowerCase().includes('boots') &&
        product?.variations?.some(
          (v) => v.is_number && typeof v.size === 'number' && v.size > 30
        )
      );
    return true;
  });

  return (
    <div className="px-4 sm:px-0">
      <div className="m-0 overflow-x-hidden pb-4">
        {visibleTabs.map((tab, index) => (
          <input
            key={tab.id}
            type="radio"
            id={`tab-${tab.id}`}
            name="product-tabs"
            defaultChecked={index === 0}
            className="hidden"
          />
        ))}

        <ul className="m-0 flex list-none items-stretch border-b border-zinc-300 p-0">
          {visibleTabs.map((tab) => (
            <li key={tab.id}>
              <label
                htmlFor={`tab-${tab.id}`}
                className="-mb-px block w-fit flex-shrink-0 flex-grow cursor-pointer whitespace-nowrap border border-b border-r-0 border-zinc-300 bg-zinc-100 p-4 text-center text-base font-semibold text-black transition-all duration-200 last:border-r hover:bg-zinc-200 sm:p-4"
              >
                <span className="hidden text-[1.8rem] sm:inline">
                  {tab.title}
                </span>
                <span className="text-[1.8rem] sm:hidden">
                  {tab.shortTitle || tab.title}
                </span>
              </label>
            </li>
          ))}
        </ul>

        {visibleTabs.map((tab) => (
          <div key={tab.id} className="hidden px-4 pb-4">
            {tab.content}
          </div>
        ))}
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
          input[type="radio"]:nth-of-type(1):checked ~ ul li:nth-of-type(1) label {
            border-left: 1px solid #d4d4d8 !important;
            border-right: 1px solid #d4d4d8 !important;
            border-top: 1px solid #d4d4d8 !important;
            border-bottom: none !important;
            background: transparent !important;
            color: black !important;
          }

          input[type="radio"]:nth-of-type(2):checked ~ ul li:nth-of-type(2) label {
            border-left: 1px solid #d4d4d8 !important;
            border-right: 1px solid #d4d4d8 !important;
            border-top: 1px solid #d4d4d8 !important;
            border-bottom: none !important;
            background: transparent !important;
            color: black !important;
          }

          input[type="radio"]:nth-of-type(3):checked ~ ul li:nth-of-type(3) label {
            border-left: 1px solid #d4d4d8 !important;
            border-right: 1px solid #d4d4d8 !important;
            border-top: 1px solid #d4d4d8 !important;
            border-bottom: none !important;
            background: transparent !important;
            color: black !important;
          }

          input[type="radio"]:nth-of-type(1):checked ~ div:nth-of-type(1) {
            display: block !important;
          }

          input[type="radio"]:nth-of-type(2):checked ~ div:nth-of-type(2) {
            display: block !important;
          }

          input[type="radio"]:nth-of-type(3):checked ~ div:nth-of-type(3) {
            display: block !important;
          }
        `,
        }}
      />
    </div>
  );
}
